/**
 * Resize an image File so its longest dimension is at most `maxDim` pixels.
 * Returns a new File (with the same mime type) or the original if it's
 * already small enough.
 *
 * Used before background-removal upload so we don't push huge phone photos
 * over the wire only to have rembg downscale them anyway.
 */
export async function resizeImageFile(
  file: File,
  maxDim: number,
): Promise<File> {
  if (!file.type.startsWith("image/")) {
    return file;
  }

  const bitmap = await loadImageBitmap(file);
  try {
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    if (longest <= maxDim) {
      return file;
    }

    const scale = maxDim / longest;
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      // Without a 2D context we can't resize; fall back to the original.
      return file;
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);

    const outType = file.type === "image/jpeg" ? "image/jpeg" : "image/png";
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve, outType, 0.92),
    );
    if (!blob) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "");
    const ext = outType === "image/jpeg" ? "jpg" : "png";
    return new File([blob], `${baseName}.${ext}`, { type: outType });
  } finally {
    if ("close" in bitmap && typeof bitmap.close === "function") {
      bitmap.close();
    }
  }
}

async function loadImageBitmap(file: File): Promise<ImageBitmap> {
  // createImageBitmap is in every browser shipped since 2017; if the runtime
  // lacks it we let the call throw and the caller falls back to the original
  // file (still uploadable, just larger than ideal).
  return createImageBitmap(file);
}
